<template>
  <div class="loginContainer">
    <dean-header :logo="headerLogo" logo-height="35"></dean-header>
    <div class="dialogContainer">
      <alert-notification/>
      <dean-dialog
          class="login-dialog"
          title="Wachtwoord instellen"
          :icon="lockIcon"
          align-top>
        <alert-notification></alert-notification>
        <span class="dean-dialog-logo" slot="dean-dialog-logo">
            <dean-icon class="deanone-logo" :glyph="headerLogo"></dean-icon>
          </span>
        <dean-input-field
            id="email"
            name="email"
            v-model="email"
            type="text"
            :disabled="true">
          E-mail
        </dean-input-field>
        <dean-input-field
            @enter="setPasswordClick"
            id="password"
            name="password"
            v-model="data.password"
            :required-field="true"
            type="password"
            :warning="$v.data.password.$error"
            @focus="$v.data.password.$touch()">
          Wachtwoord
        </dean-input-field>
        <dean-input-field
            @enter="setPasswordClick"
            id="repeatPassword"
            name="repeatPassword"
            v-model="data.repeatPassword"
            :required-field="true"
            type="password"
            :warning="$v.data.repeatPassword.$error"
            @focus="$v.data.repeatPassword.$touch()">
          Wachtwoord herhalen
        </dean-input-field>
        <span slot="dean-dialog-button-group">
          <dean-button :primary="true" @click.native="setPasswordClick">Instellen</dean-button>
        </span>
      </dean-dialog>
    </div>
  </div>
</template>

<script lang="ts">
  // VUE
  import {
    Vue,
    Component,
  } from 'vue-property-decorator';
  import axios from 'axios';

  import {
    deanButton,
    deanHeader,
    lockIcon,
    warningIcon,
    deanIcon,
  } from '@deanone/dean-components';

  import {required, minLength, sameAs, helpers} from 'vuelidate/lib/validators';
  import {Validations} from 'vuelidate-property-decorators';

  import alertNotification from '@/components/deanComponents/deanNotification.vue';
  import deanDialog from '@/components/deanComponents/deanDialog.vue';
  import deanInputField from '@/components/deanComponents/deanInputField.vue';
  import {createNotification} from '@/mixins/validations';
  import {getErrorCodeFromResponse, getErrorMessageFromResponse, getErrorMessageFromTranslationByErrorCode} from '@/mixins/errorMessage';

  // Must follow the Keycloak password policy: at least one uppercase letter, one
  // lowercase letter and one digit.
  const hasUppercase = helpers.regex('hasUppercase', /[A-Z]/);
  const hasLowercase = helpers.regex('hasLowercase', /[a-z]/);
  const hasNumber = helpers.regex('hasNumber', /\d/);

  @Component({
    components: {
      deanInputField,
      deanButton,
      deanDialog,
      deanHeader,
      deanIcon,
      alertNotification,
    },
  })

  /**
   * Set password page.
   * A newly created user lands here via the link in their "welcome" email, with the
   * email address and a (short-lived) setup token embedded in the query string.
   * The user only needs to fill in the password and password confirmation; email
   * and token are propagated straight from the URL to the backend.
   */
  export default class SetPassword extends Vue {
    public lockIcon: object = lockIcon;
    public warningIcon: object = warningIcon;
    public email: string | null = null;
    public token: string | null = null;
    public data: {
      password: string,
      repeatPassword: string,
    } = {
      password: '',
      repeatPassword: '',
    };

    @Validations()
    validations = {
      data: {
        password: {
          required,
          minLength: minLength(12),
          hasUppercase,
          hasLowercase,
          hasNumber,
        },
        repeatPassword: {required, sameAsPassword: sameAs('password')},
      },
    };

    get headerLogo() {
      return this.$store.getters.getTheme.data.loginLogo;
    }

    /**
     * Submit the new password together with the email/token taken from the URL.
     */
    public setPasswordClick() {
      this.$v.$touch();

      if (this.$v.$invalid) {
        this.$toast(createNotification(this.$v));
        return;
      }

      this.$store.dispatch('setLoadOverlay', true);
      axios.post('/-/trade/api/auth.password.set', {
        email: this.email,
        token: this.token,
        password: this.data.password,
        password_confirmation: this.data.repeatPassword,
      }).then(
        () => {
          this.$toast({
            text: this.$t('messages.passwordSetSuccess') as string,
            theme: 'success',
          });
          this.$router.push({name: 'auth.login'});
        },
        (error) => {
          const errorResponse = error?.response?.data;
          const errorCode: string | null = errorResponse ? getErrorCodeFromResponse(errorResponse) : null;
          let errorMessage: string | null = null;

          if (errorCode) {
            errorMessage = getErrorMessageFromTranslationByErrorCode(errorCode);
          }

          if (errorMessage === null && errorResponse) {
            errorMessage = getErrorMessageFromResponse(errorResponse, null);
          }

          this.$toast({
            icon: this.warningIcon,
            title: 'Fout',
            text: errorMessage || 'Er is iets misgegaan. Probeer het later nog eens.',
            theme: 'danger',
          });
        },
      ).finally(() => {
        this.$store.dispatch('setLoadOverlay', false);
      });
    }

    public created() {
      const {email, token} = this.$route.query;
      this.email = typeof email === 'string' ? email : null;
      this.token = typeof token === 'string' ? token : null;

      if (!this.email || !this.token) {
        this.$toast({
          icon: this.warningIcon,
          title: 'Fout',
          text: 'Deze link om het wachtwoord in te stellen is ongeldig.',
          theme: 'danger',
        });
      }
    }
  }
</script>

<style lang="scss" scoped>
  // IMPORT
  @import '~@deanone/dean-components/src/scss/vars';

  .login-dialog {
    background: var(--color-light-shade-1, $color-light-shade-1);
  }

  .dean-dialog-button-group > span {
    display: flex;
    justify-content: flex-end;
    padding: 5px;
  }

  .forgot-password {
    text-align: right;
    padding: 10px;
    font-size: 8pt;
    color: var(--color-link, $color-link);
    cursor: pointer;
  }

  .login-dialog {
    .dean-dialog-logo {
      display: grid;
      grid-template-columns: repeat(5, 1fr);
      margin-bottom: 15px;

      svg {
        grid-column-start: 3;
        grid-column-end: 4;
      }
    }
  }

  .deanone-logo {
    grid-column: 2 / span 4;
  }

  #actions {
    padding: 0 10px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    cursor: pointer;

    .auth-forgot-password {
      text-decoration: underline;
    }
  }
</style>
